import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * CompositionContext — persistance globale du « montage » pharmacie :
 * - Scène commune (façade + jour/nuit) partagée entre TOUS les configurateurs
 * - Une configuration complète par slug produit (croix-classique-led,
 *   lettre-boitier-alu, enseigne-interieur-neon-led, etc.)
 * - Gestion multi-projets : l'utilisateur peut sauvegarder son montage actuel
 *   sous un nom (ex: "Pharmacie de la Gare") et basculer entre plusieurs
 *   projets, tout en local (localStorage). Aucun compte requis.
 */
const STORAGE_KEY = "signapharma-composition-v1";
const PROJECTS_KEY = "signapharma-projects-v1";

const DEFAULT_SCENE = { sceneId: "facade-modern-white", customScene: null, mode: "day" };

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { scene: { ...DEFAULT_SCENE }, configs: {} };
    const parsed = JSON.parse(raw);
    return {
      scene: { ...DEFAULT_SCENE, ...(parsed.scene || {}) },
      configs: parsed.configs || {},
    };
  } catch (_e) {
    return { scene: { ...DEFAULT_SCENE }, configs: {} };
  }
}

function loadProjectsFromStorage() {
  try {
    const raw = localStorage.getItem(PROJECTS_KEY);
    if (!raw) return { projects: [], activeProjectId: null };
    const parsed = JSON.parse(raw);
    return {
      projects: Array.isArray(parsed.projects) ? parsed.projects : [],
      activeProjectId: parsed.activeProjectId || null,
    };
  } catch (_e) {
    return { projects: [], activeProjectId: null };
  }
}

const CompositionContext = createContext(null);

export function CompositionProvider({ children }) {
  const [state, setState] = useState(() => loadFromStorage());
  const [projectsState, setProjectsState] = useState(() => loadProjectsFromStorage());
  const [previousSlug, setPreviousSlug] = useState(null);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_e) { /* quota */ }
  }, [state]);

  useEffect(() => {
    try { localStorage.setItem(PROJECTS_KEY, JSON.stringify(projectsState)); } catch (_e) { /* quota */ }
  }, [projectsState]);

  const getConfigFor = useCallback((slug) => {
    const own = state.configs[slug] || {};
    return {
      ...own,
      sceneId: state.scene.sceneId,
      customScene: state.scene.customScene,
      mode: state.scene.mode,
    };
  }, [state]);

  const setConfigFor = useCallback((slug, payload) => {
    setState((prev) => {
      const next = { ...prev, configs: { ...prev.configs, [slug]: payload } };
      const sceneFields = {};
      if (payload && Object.prototype.hasOwnProperty.call(payload, "sceneId")) sceneFields.sceneId = payload.sceneId;
      if (payload && Object.prototype.hasOwnProperty.call(payload, "customScene")) sceneFields.customScene = payload.customScene;
      if (payload && Object.prototype.hasOwnProperty.call(payload, "mode")) sceneFields.mode = payload.mode;
      if (Object.keys(sceneFields).length > 0) {
        next.scene = { ...prev.scene, ...sceneFields };
      }
      return next;
    });
  }, []);

  const summary = useMemo(() => {
    const out = { cross: 0, boitier: 0, retro: 0, inox: 0, chassis: 0, neon: 0, total: 0, slugs: [] };
    for (const [slug, cfg] of Object.entries(state.configs)) {
      if (!cfg) continue;
      let count = 0;
      if (Array.isArray(cfg.items) && cfg.items.length > 0) {
        count = cfg.items.filter((it) => !(it?.designId && String(it.designId).startsWith("_empty_"))).length;
      }
      else if (Array.isArray(cfg.lines) && cfg.lines.some((L) => (L?.text || "").trim())) {
        count = cfg.lines.filter((L) => (L?.text || "").trim()).length;
      }
      else if (typeof cfg.text === "string" && cfg.text.trim()) count = 1;
      if (count <= 0) continue;
      out.total += count;
      out.slugs.push({ slug, count });
      if (slug === "croix-neon-led" || slug === "croix-classique-led" || slug === "croix-point-a-point") out.cross += count;
      else if (slug === "lettre-boitier-alu") out.boitier += count;
      else if (slug === "lettre-retro-eclaire") out.retro += count;
      else if (slug === "lettre-inox-3d") out.inox += count;
      else if (slug === "enseigne-boitier-chassis") out.chassis += count;
      else if (slug === "enseigne-interieur-neon-led") out.neon += count;
    }
    return out;
  }, [state.configs]);

  // ───────────────────── Gestion multi-projets ─────────────────────
  // Sauvegarde l'état courant (scene + configs) sous un nouveau projet nommé.
  const saveAsNewProject = useCallback((name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return null;
    const id = `p-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const now = new Date().toISOString();
    const project = {
      id, name: trimmed, createdAt: now, updatedAt: now,
      scene: state.scene, configs: state.configs,
    };
    setProjectsState((prev) => ({
      projects: [...prev.projects, project],
      activeProjectId: id,
    }));
    return id;
  }, [state]);

  // Sauvegarde sur le projet ACTIF (overwrite). Si pas de projet actif, no-op.
  const saveActiveProject = useCallback(() => {
    setProjectsState((prev) => {
      if (!prev.activeProjectId) return prev;
      const idx = prev.projects.findIndex((p) => p.id === prev.activeProjectId);
      if (idx < 0) return prev;
      const updated = {
        ...prev.projects[idx],
        scene: state.scene,
        configs: state.configs,
        updatedAt: new Date().toISOString(),
      };
      const projects = [...prev.projects];
      projects[idx] = updated;
      return { ...prev, projects };
    });
  }, [state]);

  // Charge un projet sauvegardé dans l'état courant. Reload pour ré-initialiser
  // proprement les configurateurs (qui ont leur propre useState local).
  const loadProject = useCallback((id) => {
    const p = projectsState.projects.find((x) => x.id === id);
    if (!p) return false;
    setState({ scene: p.scene || { ...DEFAULT_SCENE }, configs: p.configs || {} });
    setProjectsState((prev) => ({ ...prev, activeProjectId: id }));
    // Reload après un court délai pour laisser le useEffect persister state.
    if (typeof window !== "undefined") {
      setTimeout(() => { try { window.location.reload(); } catch (_e) {} }, 80);
    }
    return true;
  }, [projectsState.projects]);

  // Supprime un projet de la liste. Si c'est l'actif, on dé-active.
  const deleteProject = useCallback((id) => {
    setProjectsState((prev) => ({
      projects: prev.projects.filter((p) => p.id !== id),
      activeProjectId: prev.activeProjectId === id ? null : prev.activeProjectId,
    }));
  }, []);

  // Renomme un projet existant.
  const renameProject = useCallback((id, newName) => {
    const trimmed = (newName || "").trim();
    if (!trimmed) return;
    setProjectsState((prev) => ({
      ...prev,
      projects: prev.projects.map((p) =>
        p.id === id ? { ...p, name: trimmed, updatedAt: new Date().toISOString() } : p
      ),
    }));
  }, []);

  // Nouveau projet : vide complètement les caches signapharma-* + reload.
  // L'utilisateur repart d'un montage vierge sans projet actif.
  const newProject = useCallback(() => {
    try {
      Object.keys(localStorage).forEach((k) => {
        // Conserve UNIQUEMENT la liste des projets ET l'email du visiteur
        // — tout le reste est vidé. (Spec : email demandé une seule fois.)
        if (k.startsWith("signapharma")
            && k !== PROJECTS_KEY
            && k !== "signapharma-visitor-email-v1"
            && k !== "signapharma-email-gate-asked-v1") {
          localStorage.removeItem(k);
        }
      });
      // Désactive le projet courant.
      setProjectsState((prev) => ({ ...prev, activeProjectId: null }));
    } catch (_e) { /* ignore */ }
    if (typeof window !== "undefined") {
      setTimeout(() => { try { window.location.reload(); } catch (_e) {} }, 80);
    }
  }, []);

  // Reset complet (purge TOUT, projets inclus). Gardé pour le bouton existant
  // "Réinitialiser" qui sert à effacer le montage courant. Pour le nouveau
  // bouton "Nouveau projet" on utilise `newProject` ci-dessus.
  const reset = useCallback(() => {
    try {
      Object.keys(localStorage).forEach((k) => {
        // Reset n'efface PAS la liste des projets ni l'email du visiteur :
        //  - Projets : on garde la possibilité de revenir aux projets sauvegardés
        //  - Email   : déjà capturé, on ne redemande JAMAIS au visiteur même
        //              après un reset (cf. spec "demander qu'une fois")
        if (k.startsWith("signapharma")
            && k !== PROJECTS_KEY
            && k !== "signapharma-visitor-email-v1"
            && k !== "signapharma-email-gate-asked-v1") {
          localStorage.removeItem(k);
        }
      });
    } catch (_e) { /* ignore */ }
    if (typeof window !== "undefined") {
      try { window.location.reload(); } catch (_e) { /* ignore */ }
    }
  }, []);

  const activeProject = useMemo(() =>
    projectsState.projects.find((p) => p.id === projectsState.activeProjectId) || null,
    [projectsState]
  );

  const value = useMemo(() => ({
    scene: state.scene,
    configs: state.configs,
    summary,
    getConfigFor,
    setConfigFor,
    reset,
    previousSlug,
    setPreviousSlug,
    // Multi-projets
    projects: projectsState.projects,
    activeProjectId: projectsState.activeProjectId,
    activeProject,
    saveAsNewProject,
    saveActiveProject,
    loadProject,
    deleteProject,
    renameProject,
    newProject,
  }), [
    state.scene, state.configs, summary, getConfigFor, setConfigFor, reset, previousSlug,
    projectsState, activeProject, saveAsNewProject, saveActiveProject, loadProject,
    deleteProject, renameProject, newProject,
  ]);

  return <CompositionContext.Provider value={value}>{children}</CompositionContext.Provider>;
}

export function useComposition() {
  const ctx = useContext(CompositionContext);
  if (!ctx) throw new Error("useComposition must be used within CompositionProvider");
  return ctx;
}
